'use client'
import { ValueWithUnit } from '@repo/shared/components'
import {
  AspectRatio,
  Button,
  Col,
  Row,
  Slider,
  Typography,
} from '@repo/ui/components'
import type { UserAccountPortfolio, UserProject } from '@repo/services'
import Image from 'next/image'
import { globalConfig } from '@repo/shared/utils'
import { useTranslate } from '@repo/shared/hooks'
import { DeliverDialog } from './deliver.dialog'
import { CertificateDialog } from '@repo/certificate/containers'
import { useState } from 'react'

export const WalletProjectPropertyCardItem = ({
  project,
  userProject,
}: {
  project: UserProject
  userProject: UserAccountPortfolio
}) => {
  const { t } = useTranslate()
  const [isCertificateDialogOpen, setIsCertificateDialogOpen] = useState(false)

  if (!project) return null

  const url = project?.logoURL
    ? `${globalConfig.baseImageUrl}${project?.logoURL}`
    : '/images/placeholder.webp'

  return (
    <Row className='gap-md '>
      <Col className='w-fit'>
        <div className='w-[43px] h-[64px]'>
          <AspectRatio className='aspect-3/4'>
            <Image
              src='/images/document-property.webp'
              alt='Document'
              fill
              className='h-full w-full  rounded-2xs object-cover'
            />
          </AspectRatio>
        </div>
      </Col>
      <Col className='gap-sm border-b border-neutral-surface-5 py-md'>
        <Row className='justify-between'>
          <Typography size='xs' weight='bold' color='text-neutral-surface-100'>
            {project.displayName}
          </Typography>
          <div className='w-lg'>
            <AspectRatio className='aspect-1/1'>
              <Image
                src={url}
                alt='Photo of property'
                fill
                sizes='(max-width: 768px) 100vw, 32px'
                className='h-full w-full  rounded-2xs object-cover'
              />
            </AspectRatio>
          </div>
        </Row>
        <Row>
          <Slider
            value={[userProject.availableBalance || 0]}
            max={project?.market?.maxSupply || 0}
            color='base'
            noThumb
          />
        </Row>
        <Row className='justify-between'>
          <Row className='gap-xs w-fit items-center'>
            {/* wallet: projects tab */}
            <Typography size='2xs' color='text-neutral-surface-80'>
              {t('walletPage_your_share')}
            </Typography>

            <ValueWithUnit
              value={(userProject.availableBalance || 0) / 10_000}
              unit='m2'
              scale={project.scale}
              decimalPlaces={4}
            />
          </Row>

          <Typography size='2xs' color='text-neutral-surface-80'>
            {t('walletPage_your_share_with')}
          </Typography>
          <ValueWithUnit
            value={(project?.market?.maxSupply || 0) / 10_000}
            unit='m2'
            scale={project.scale}
            decimalPlaces={6}
          />
        </Row>

        <Row className='justify-between gap-sm'>
          <Button
            variant='ghost'
            color='base'
            size='xs'
            className='w-1/2'
            onClick={() => setIsCertificateDialogOpen(true)}
          >
            {t('walletPage_certificate_action')}
          </Button>
          <DeliverDialog project={project} userProject={userProject} />
        </Row>
      </Col>

      {/* Certificate Dialog */}
      <CertificateDialog
        open={isCertificateDialogOpen}
        onOpenChange={setIsCertificateDialogOpen}
        project={project}
        userProject={userProject}
      />
    </Row>
  )
}
